import React, { useState, useEffect } from 'react';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Settings, 
  Clock, 
  Calculator, 
  TrendingUp,
  Users,
  ArrowRight,
  RefreshCw,
  Sparkles,
  BarChart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await API.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleGenerateMockData = async () => {
    setGenerating(true);
    try {
      const response = await API.post('/mock/generate');
      toast.success(`Mock data generated: ${response.data.entries_created} timesheet entries created`);
      fetchStats();
    } catch (error) {
      toast.error('Failed to generate mock data');
    } finally {
      setGenerating(false);
    }
  };

  const statCards = [
    {
      title: 'EBA Documents',
      value: stats?.eba_documents || 0,
      icon: FileText,
      color: 'bg-blue-500',
      href: '/eba'
    },
    {
      title: 'Active Rules',
      value: stats?.active_rules || 0,
      icon: Settings,
      color: 'bg-emerald-500',
      href: '/rules'
    },
    {
      title: 'Timesheets',
      value: stats?.timesheets_processed || 0,
      icon: Clock,
      color: 'bg-amber-500',
      href: '/timesheets'
    },
    {
      title: 'Employees Processed',
      value: stats?.unique_employees || 0,
      icon: Users,
      color: 'bg-purple-500',
      href: '/payroll'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-equipr-yellow">
              <BarChart className="w-5 h-5 text-equipr-black" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Welcome back, {user?.name}. Here&apos;s your payroll overview.</p>
        </div>
        <Button 
          onClick={handleGenerateMockData} 
          disabled={generating}
          variant="outline"
          className="gap-2"
          data-testid="generate-mock-btn"
        >
          {generating ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          Generate Mock Data
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title}
            className="border-slate-200 hover-card cursor-pointer animate-fade-in-up"
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={() => navigate(stat.href)}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-heading font-bold text-primary mt-1">
                    {loading ? '-' : stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Hours Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="border-slate-200 animate-fade-in-up-delay-1">
          <CardHeader>
            <CardTitle className="text-lg font-heading font-semibold text-primary">
              Hours Summary
            </CardTitle>
            <CardDescription>Recent payroll processing results</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded border border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Ordinary Hours
                </p>
                <p className="text-2xl font-heading font-bold text-slate-900 mt-1">
                  {loading ? '-' : stats?.total_ordinary_hours?.toFixed(1) || 0}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded border border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Overtime Hours
                </p>
                <p className="text-2xl font-heading font-bold text-slate-900 mt-1">
                  {loading ? '-' : stats?.total_overtime_hours?.toFixed(1) || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 animate-fade-in-up-delay-2">
          <CardHeader>
            <CardTitle className="text-lg font-heading font-semibold text-primary">
              Quick Actions
            </CardTitle>
            <CardDescription>Common tasks and workflows</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-between group"
              onClick={() => navigate('/eba')}
              data-testid="quick-action-upload-eba"
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Upload EBA Document
              </span>
              <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between group"
              onClick={() => navigate('/timesheets')}
              data-testid="quick-action-import-timesheet"
            >
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Import Timesheet
              </span>
              <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-between group"
              onClick={() => navigate('/payroll')}
              data-testid="quick-action-process-payroll"
            >
              <span className="flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Process Payroll
              </span>
              <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Timesheets */}
      {stats?.recent_timesheets?.length > 0 && (
        <Card className="border-slate-200 animate-fade-in-up-delay-2">
          <CardHeader>
            <CardTitle className="text-lg font-heading font-semibold text-primary">
              Recent Timesheets
            </CardTitle>
            <CardDescription>Latest imported timesheet files</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_timesheets.map((ts) => (
                <div 
                  key={ts.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-100"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{ts.filename}</p>
                      <p className="text-xs text-slate-500">
                        {formatDate(ts.created_at)}
                      </p>
                    </div>
                  </div>
                  <span className={`status-badge ${
                    ts.status === 'processed' ? 'status-badge-success' : 'status-badge-info'
                  }`}>
                    {ts.status}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
